import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { useApiCitiesAndNeighborhoodsList } from '@rover/rsdk/src/apiClient/latest/api-cities-and-neighborhoods-list';
import { useApiFrontendServiceTypesList } from '@rover/rsdk/src/apiClient/latest/api-frontend-service-types-list';
import type { BrandLocalityServiceCitiesItem } from '@rover/rsdk/src/apiClient/latest/model/brandLocalityServiceCitiesItem';
import type { CountryConfiguration } from '@rover/rsdk/src/apiClient/latest/model/countryConfiguration';
import type { CountryFooterLink } from '@rover/rsdk/src/apiClient/latest/model/countryFooterLink';
import type { FrontendConfigurationSettings } from '@rover/rsdk/src/apiClient/latest/model/frontendConfigurationSettings';
import type { LocalizedBrandService } from '@rover/rsdk/src/apiClient/latest/model/localizedBrandService';
import type { PaginatedCountryRegionLinkList } from '@rover/rsdk/src/apiClient/latest/model/paginatedCountryRegionLinkList';
import type { PaginatedServiceTypeList } from '@rover/rsdk/src/apiClient/latest/model/paginatedServiceTypeList';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';
import useCountryFooterLinksShim from '@rover/shared/js/hooks/useCountryFooterLinksShim';
import useCountryRegionLinksShim from '@rover/shared/js/hooks/useCountryRegionLinksShim';
import useFrontendConfigurationShim from '@rover/shared/js/hooks/useFrontendConfigurationShim';
import type { ServiceLandingPathDescriptor } from '@rover/shared/js/hooks/useServiceLanding';

export type ServiceLandingPageData = {
  citiesAndNeighborhoods: BrandLocalityServiceCitiesItem[] | undefined;
  countryConfig: CountryConfiguration | null | undefined;
  countryFooterLinks: CountryFooterLink[] | undefined;
  countryRegionLinks: PaginatedCountryRegionLinkList | undefined;
  locale: Locale;
  settings: FrontendConfigurationSettings | undefined;
  serviceTypes: PaginatedServiceTypeList | undefined;
  isLoading: boolean;
  isError: boolean;
};

/**
 * The queries every service landing engine needs, whichever template it renders. This is the
 * runtime twin of the shared prefetch in `BaseServiceLandingContainer`: a query added to one
 * belongs in the other.
 */
const useServiceLandingPageData = (
  descriptor: ServiceLandingPathDescriptor,
  serviceLandingData: LocalizedBrandService
): ServiceLandingPageData => {
  const { language: locale = Locale.EnUs, settings } = useFrontendConfigurationShim();
  const { navigation: countryConfig } = useCountryConfigurationShim();
  const { results: countryFooterLinks } = useCountryFooterLinksShim();
  const countryRegionLinks = useCountryRegionLinksShim();

  const {
    data: citiesAndNeighborhoods,
    isLoading: isLoadingCitiesAndNeighborhoods,
    isError: isErrorCitiesAndNeighborhoods,
  } = useApiCitiesAndNeighborhoodsList({
    countryUrlPrefix: descriptor.countryUrlPrefix,
    // API expects the seo slug, not the internal service type (i.e. "pet-sitting" not "overnight-boarding")
    serviceType: serviceLandingData.serviceLandingUrlSlug,
  });

  const {
    data: serviceTypes,
    isLoading: isLoadingServiceTypes,
    isError: isErrorServiceTypes,
  } = useApiFrontendServiceTypesList();

  return {
    citiesAndNeighborhoods: citiesAndNeighborhoods?.results,
    countryConfig,
    countryFooterLinks,
    countryRegionLinks,
    locale,
    settings,
    serviceTypes,
    isLoading: isLoadingCitiesAndNeighborhoods || isLoadingServiceTypes,
    isError: isErrorCitiesAndNeighborhoods || isErrorServiceTypes,
  };
};

export default useServiceLandingPageData;
